<template>
  <div>
    <div v-if="loading" class="text-center py-5">
      <b-spinner class="align-middle text-primary" />
    </div>

    <b-alert v-else-if="error" show variant="warning">
      {{ error }}
    </b-alert>

    <template v-else-if="pack">
      <!-- Шапка: информационные поля -->
      <b-card no-body>
        <b-card-header class="pb-50">
          <div class="d-flex justify-content-between align-items-center w-100">
            <h4 class="mb-0">{{ pack.name }}</h4>
            <b-button
                v-if="pack.has_file"
                variant="outline-primary"
                size="sm"
                @click="downloadPack"
            >
              <feather-icon icon="DownloadIcon" class="mr-25" />
              Скачать
            </b-button>
          </div>
        </b-card-header>
        <b-card-body>
          <b-row>
            <b-col cols="6" md="2" class="mb-1">
              <div class="text-muted font-small-3">ID</div>
              <div class="font-weight-bold">{{ pack.id }}</div>
            </b-col>
            <b-col cols="6" md="2" class="mb-1">
              <div class="text-muted font-small-3">Тип</div>
              <div class="font-weight-bold">{{ packTypes[pack.type] || pack.type }}</div>
            </b-col>
            <b-col cols="6" md="2" class="mb-1">
              <div class="text-muted font-small-3">Статус</div>
              <b-badge :variant="statusVariant(pack.status)">{{ statusLabel(pack.status) }}</b-badge>
              <b-badge v-if="pack.is_archived" variant="light-secondary" class="ml-25">Архив</b-badge>
            </b-col>
            <b-col cols="6" md="2" class="mb-1">
              <div class="text-muted font-small-3">Пользователь</div>
              <div class="font-weight-bold">{{ pack.user ? pack.user.name : '—' }}</div>
              <small v-if="pack.user" class="text-muted">{{ pack.user.email }}</small>
            </b-col>
            <b-col cols="6" md="2" class="mb-1">
              <div class="text-muted font-small-3">Создан</div>
              <div class="font-weight-bold">{{ formatDate(pack.created_at) }}</div>
            </b-col>
            <b-col cols="6" md="2" class="mb-1">
              <div class="text-muted font-small-3">Обновлён</div>
              <div class="font-weight-bold">{{ formatDate(pack.updated_at) }}</div>
            </b-col>
          </b-row>

          <b-alert v-if="packErrors.length" show variant="danger" class="mt-1">
            <h6 class="alert-heading">Ошибки генерации</h6>
            <div class="alert-body">
              <div v-for="(packError, index) in packErrors" :key="index">{{ packError }}</div>
            </div>
          </b-alert>

          <b-alert v-if="packWarnings.length" show variant="warning" class="mt-1">
            <h6 class="alert-heading">Предупреждения генерации</h6>
            <div class="alert-body">
              <ul class="mb-0 pl-1">
                <li v-for="(packWarning, index) in packWarnings" :key="index">{{ packWarning }}</li>
              </ul>
            </div>
          </b-alert>

          <!-- Прогресс генерации, пока пак в работе -->
          <div v-if="progressRows.length" class="mt-1">
            <div v-for="progressRow in progressRows" :key="progressRow.type" class="mb-75">
              <div class="d-flex justify-content-between font-small-3 mb-25">
                <span class="font-weight-bold">{{ progressRow.label }}</span>
                <span class="text-muted">{{ progressRow.current }} / {{ progressRow.total }}</span>
              </div>
              <b-progress
                  :value="progressRow.current"
                  :max="progressRow.total || 1"
                  height="8px"
                  animated
                  variant="primary"
              />
              <small v-if="progressRow.comment" class="text-muted">{{ progressRow.comment }}</small>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Конфигурация -->
      <b-card no-body>
        <b-card-header class="pb-50">
          <h5 class="mb-0">Конфигурация</h5>
        </b-card-header>
        <b-card-body>
          <p v-if="!configRows.length && !themeTables.length" class="text-muted mb-0">
            Конфигурация отсутствует
          </p>

          <b-row>
            <b-col
                v-for="row in configRows"
                :key="row.key"
                cols="6"
                md="3"
                class="mb-1"
            >
              <div class="text-muted font-small-3">{{ row.label }}</div>
              <div class="font-weight-bold">{{ row.value }}</div>
            </b-col>
          </b-row>

          <div v-for="themeTable in themeTables" :key="themeTable.title" class="mt-1">
            <h6>{{ themeTable.title }}</h6>
            <b-table-simple small responsive class="mb-1">
              <b-thead>
                <b-tr>
                  <b-th style="width: 80px">№</b-th>
                  <b-th>Тип темы</b-th>
                  <b-th>Формат</b-th>
                  <b-th style="width: 110px" class="text-center">Для всех</b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr v-for="theme in themeTable.themes" :key="theme.number">
                  <b-td>{{ theme.number }}</b-td>
                  <b-td>{{ theme.type }}</b-td>
                  <b-td>{{ theme.format }}</b-td>
                  <b-td class="text-center">
                    <feather-icon v-if="theme.forAll" icon="CheckIcon" class="text-success" />
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </div>
        </b-card-body>
      </b-card>

      <!-- Свои плейлисты: карточка сама решает, показываться ли -->
      <pack-custom-pool :pack-id="pack.id" :pool="customPool" />

      <!-- Содержимое -->
      <b-card no-body>
        <b-card-header class="pb-50">
          <h5 class="mb-0">
            Содержимое<span v-if="contentRows.length" class="text-muted font-small-3 ml-50">({{ contentRows.length }})</span>
          </h5>
        </b-card-header>
        <b-table
            :items="contentRows"
            :fields="contentColumns"
            responsive
            show-empty
            empty-text="Содержимое отсутствует"
            primary-key="number"
            class="position-relative"
        >
          <template #cell(show_details)="row">
            <b-button
                variant="flat-secondary"
                size="sm"
                class="btn-icon"
                :title="row.detailsShowing ? 'Скрыть детали' : 'Показать детали'"
                @click="row.toggleDetails"
            >
              <feather-icon :icon="row.detailsShowing ? 'ChevronDownIcon' : 'ChevronRightIcon'" />
            </b-button>
          </template>

          <template #cell(price)="data">
            <span class="text-nowrap">
              {{ data.item.price }}
              <span v-if="data.item.is_auction" title="Вопрос со ставкой">
                <feather-icon icon="DollarSignIcon" class="text-warning ml-25" />
              </span>
              <span v-if="data.item.is_for_all" title="Для всех">
                <feather-icon icon="UsersIcon" class="text-info ml-25" />
              </span>
            </span>
          </template>

          <template #cell(format)="data">
            {{ themeFormatLabel(data.item.format || data.item.question_type) }}
          </template>

          <template #cell(question_type)="data">
            {{ themeFormatLabel(data.item.question_type) }}
          </template>

          <template #row-details="row">
            <div class="border rounded p-1">
              <b-row>
                <b-col cols="12" md="6" class="mb-50">
                  <div class="text-muted font-small-3">Подсказка</div>
                  <div>{{ row.item.hint || '—' }}</div>
                </b-col>
                <b-col cols="12" md="6" class="mb-50">
                  <div class="text-muted font-small-3">Комментарий</div>
                  <div>{{ row.item.comment || '—' }}</div>
                </b-col>
                <b-col cols="12" md="6" class="mb-50">
                  <div class="text-muted font-small-3">Особенности</div>
                  <b-badge v-if="row.item.is_auction" variant="light-warning" class="mr-25">Ставка</b-badge>
                  <b-badge v-if="row.item.is_for_all" variant="light-info" class="mr-25">Для всех</b-badge>
                  <span v-if="!row.item.is_auction && !row.item.is_for_all">—</span>
                </b-col>
                <b-col cols="6" md="3" class="mb-50">
                  <div class="text-muted font-small-3">Сложность</div>
                  <div>{{ displayValue(row.item.difficulty) }}</div>
                </b-col>
                <b-col cols="6" md="3" class="mb-50">
                  <div class="text-muted font-small-3">Популярность</div>
                  <div>{{ displayValue(row.item.popularity) }}</div>
                </b-col>
                <b-col cols="12" md="6" class="mb-50">
                  <div class="text-muted font-small-3">Варианты ответа</div>
                  <ol v-if="answerOptions(row.item).length" class="mb-0 pl-1">
                    <li v-for="(answerOption, optionIndex) in answerOptions(row.item)" :key="optionIndex">
                      {{ answerOption }}
                    </li>
                  </ol>
                  <div v-else>—</div>
                </b-col>
                <b-col cols="12" md="6" class="mb-50">
                  <div class="text-muted font-small-3">Артисты</div>
                  <div v-if="row.item.artist_ids && row.item.artist_ids.length">
                    <b-link
                        v-for="artistId in row.item.artist_ids"
                        :key="artistId"
                        :to="{ name: 'artist-view', params: { id: artistId } }"
                        class="mr-50"
                    >
                      #{{ artistId }}
                    </b-link>
                  </div>
                  <div v-else>—</div>
                </b-col>
              </b-row>
            </div>
          </template>
        </b-table>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BLink,
  BProgress,
  BRow,
  BSpinner,
  BTable,
  BTableSimple,
  BTbody,
  BTd,
  BTh,
  BThead,
  BTr,
} from 'bootstrap-vue'
import store from '@/store'
import { computed, onUnmounted, ref } from '@vue/composition-api'
import generatorStoreModule from '@/views/generator/generatorStoreModule'
import PackCustomPool from '@/views/generator/PackCustomPool.vue'
import {
  CLUSTERS,
  LAYOUTS,
  PACK_PRESETS,
  PACK_STATUSES,
  PACK_TYPES,
  PRICE_FORMATS,
  THEME_FORMATS,
  THEME_TYPES,
} from '@/views/generator/packDictionaries'

const boolLabel = value => (value ? 'Да' : 'Нет')

// [1980, 1990] → «1980-е, 1990-е»
const decadesLabel = value => {
  if (!Array.isArray(value) || !value.length) return '—'
  return [...value].sort((first, second) => first - second).map(decade => `${decade}-е`).join(', ')
}

// Подписи скалярных опций конфига (источник: generator-front)
const OPTION_LABELS = {
  layout: { label: 'Компоновка', format: value => LAYOUTS[value] || value },
  cluster: { label: 'Кластер', format: value => CLUSTERS[value] || value },
  difficulty: { label: 'Сложность', format: String },
  onlyRu: { label: 'Только RU', format: boolLabel },
  onlyEn: { label: 'Только EN', format: boolLabel },
  addAbbreviationHints: { label: 'Подсказки-аббревиатуры', format: boolLabel },
  addAntiShazam: { label: 'Антишазам', format: boolLabel },
  auctions: { label: 'Вопросы со ставками', format: boolLabel },
  melodyChoiceOptionsCount: { label: 'Вариантов в «1 из N»', format: String },
  covers: { label: 'Темы с каверами (в раунде)', format: String },
  coverArts: { label: 'Темы с обложками (в раунде)', format: String },
  snakes: { label: 'Темы-змейки (в раунде)', format: String },
  finalCovers: { label: 'Кавер-темы финала', format: String },
  finalCoverArts: { label: 'Темы с обложками финала', format: String },
  preset: { label: 'Пресет пакета', format: value => PACK_PRESETS[value] || value },
  finalCount: { label: 'Кол-во тем в финале', format: String },
  frameCount: { label: 'Число кадров', format: String },
  addAbbreviationHintsCommon: { label: 'Подсказки-аббревиатуры', format: boolLabel },
  priceFormat: { label: 'Формат пакета', format: value => PRICE_FORMATS[value] || value },
  forAll: { label: 'Для всех', format: boolLabel },
  decades: { label: 'Декады', format: decadesLabel },
  gapoifikaTrackCount: { label: 'Треков в ГаПоИФиКе', format: String },
}

// Опции, которые выводятся отдельными блоками, а не строкой конфигурации
const SEPARATELY_RENDERED_OPTIONS = ['customPool']

// Статусы пака, при которых генерация ещё идёт и есть смысл показывать прогресс
const IN_PROGRESS_STATUSES = ['new', 'pending', 'processing', 'build']

const PROGRESS_TYPE_LABELS = {
  prepare: 'Подготовка',
  build: 'Сборка',
}

// Предупреждение генерации: строка как есть, у объекта — message/text, иначе JSON
const formatWarning = warning => {
  if (warning === null || warning === undefined) return ''
  if (typeof warning === 'string') return warning
  if (typeof warning === 'object') return warning.message || warning.text || JSON.stringify(warning)
  return String(warning)
}

// Вариант ответа: строка как есть, у объекта — text/title/answer/name, иначе JSON
const formatAnswerOption = answerOption => {
  if (answerOption === null || answerOption === undefined) return ''
  if (typeof answerOption === 'object') {
    return answerOption.text || answerOption.title || answerOption.answer || answerOption.name || JSON.stringify(answerOption)
  }
  return String(answerOption)
}

const THEME_LIST_LABELS = {
  roundThemes: 'Темы раунда',
  allRoundThemes: 'Темы всех раундов',
  finalThemes: 'Темы финала',
}

export default {
  components: {
    PackCustomPool,
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCol,
    BLink,
    BProgress,
    BRow,
    BSpinner,
    BTable,
    BTableSimple,
    BTbody,
    BTd,
    BTh,
    BThead,
    BTr,
  },
  setup(props, { root }) {
    const STORE_MODULE_NAME = 'app-generator'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, generatorStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const pack = ref(null)
    const loading = ref(true)
    const error = ref(null)

    const contentColumns = [
      { key: 'show_details', label: '', thStyle: { width: '40px' } },
      { key: 'number', label: '№ п/п', sortable: true },
      { key: 'round', label: 'Раунд', sortable: true },
      { key: 'topic', label: 'Тема', sortable: true },
      { key: 'price', label: 'Стоимость', sortable: true },
      { key: 'format', label: 'Формат', sortable: true },
      { key: 'question_type', label: 'Формат вопроса', sortable: true },
      { key: 'answer', label: 'Ответ', sortable: true },
    ]

    const options = computed(() => {
      if (!pack.value || !pack.value.config) return {}
      return pack.value.config.options || {}
    })

    const configRows = computed(() => Object.keys(options.value)
      .filter(key => !(key in THEME_LIST_LABELS) && !SEPARATELY_RENDERED_OPTIONS.includes(key))
      .map(key => {
        const known = OPTION_LABELS[key]
        const value = options.value[key]
        return {
          key,
          label: known ? known.label : key,
          value: known ? known.format(value) : JSON.stringify(value),
        }
      }))

    const themeTables = computed(() => Object.keys(THEME_LIST_LABELS)
      .filter(key => Array.isArray(options.value[key]) && options.value[key].length)
      .map(key => ({
        title: THEME_LIST_LABELS[key],
        themes: options.value[key].map((theme, index) => ({
          number: index + 1,
          type: THEME_TYPES[theme.type] || theme.type,
          format: THEME_FORMATS[theme.format] || theme.format,
          forAll: Boolean(theme.forAll),
        })),
      })))

    const contentRows = computed(() => (pack.value && pack.value.content_rows) || [])

    const customPool = computed(() => options.value.customPool || null)

    const packWarnings = computed(() => {
      if (!pack.value || !pack.value.warnings) return []
      const warnings = Array.isArray(pack.value.warnings) ? pack.value.warnings : [pack.value.warnings]
      return warnings.map(formatWarning).filter(warning => warning !== '')
    })

    const progressRows = computed(() => {
      if (!pack.value || !IN_PROGRESS_STATUSES.includes(pack.value.status)) return []
      if (!Array.isArray(pack.value.progress)) return []
      return pack.value.progress.map(progressItem => ({
        type: progressItem.type,
        label: PROGRESS_TYPE_LABELS[progressItem.type] || progressItem.type,
        current: Number(progressItem.current) || 0,
        total: Number(progressItem.total) || 0,
        comment: progressItem.comment,
      }))
    })

    const themeFormatLabel = code => (code ? (THEME_FORMATS[code] || code) : '—')
    const answerOptions = item => (Array.isArray(item.answer_options) ? item.answer_options : [])
      .map(formatAnswerOption)
      .filter(answerOption => answerOption !== '')
    const displayValue = value => (value === null || value === undefined || value === '' ? '—' : value)

    const packErrors = computed(() => {
      if (!pack.value || !pack.value.errors) return []
      return Array.isArray(pack.value.errors) ? pack.value.errors : [JSON.stringify(pack.value.errors)]
    })

    const statusLabel = status => (PACK_STATUSES[status] ? PACK_STATUSES[status].label : status)
    const statusVariant = status => (PACK_STATUSES[status] ? PACK_STATUSES[status].variant : 'light-secondary')
    const formatDate = value => (value ? new Date(value).toLocaleString('ru-RU') : '—')

    const downloadPack = () => {
      store.dispatch('app-generator/downloadPack', { id: pack.value.id })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${pack.value.name} [${pack.value.id}].siq`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        })
    }

    store.dispatch('app-generator/fetchPackDetails', { id: root.$route.params.id })
      .then(response => {
        pack.value = response.data.data
      })
      .catch(fetchError => {
        error.value = (fetchError.response && fetchError.response.data && fetchError.response.data.message)
          || 'Не удалось загрузить пакет'
      })
      .finally(() => {
        loading.value = false
      })

    return {
      pack,
      loading,
      error,
      contentColumns,
      configRows,
      themeTables,
      contentRows,
      packErrors,
      packWarnings,
      progressRows,
      customPool,
      packTypes: PACK_TYPES,

      themeFormatLabel,
      answerOptions,
      displayValue,

      statusLabel,
      statusVariant,
      formatDate,
      downloadPack,
    }
  },
}
</script>
